import type { Address } from "viem"
import { readContract } from "@wagmi/core"
import { action, computed, makeObservable, observable, runInAction } from "mobx"
import { formatUnits } from "viem"

import type { ApprovalMode, ContractRiskLevel } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import type { OrchestratedQueryStore, RateLimitConfig } from "@/domains/library"
import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import type { TokenStore } from "@/domains/tokens"
import type { IWalletConnectionStore } from "@/domains/wallet/types"
import { erc20Abi } from "@/domains/abis"
import { determineApprovalAmount, OrchestratedQuery, unixTimestamp } from "@/domains/library"
import { openOceanGasPriceResponse, openOceanQuoteResponse } from "@/domains/validators"

import type {
  IPriceImpactCalculator,
  ISwapAggregatorPlugin,
  ISwapAggregatorSettingsStore,
  ISwapCoordinator,
  ISwapInputStore,
} from "../interfaces"
import type {
  AggregatorQuoteRequest,
  AggregatorTransactionRequest,
  NormalizedSwapQuote,
  NormalizedSwapTransaction,
  SwapQuoteRequestData,
} from "../types"
import {
  SwapAggregatorDisabledEvent,
  SwapAggregatorEnabledEvent,
  SwapApprovalCompletedEvent,
  SwapApprovalStartedEvent,
  SwapErrorEvent,
  SwapQuoteReceivedEvent,
  SwapQuoteRequestClearedEvent,
  SwapQuoteRequestedEvent,
} from "../events"

type OpenOceanConfig = {
  swapEndpoint: string
  gasEndpoint: string
  nativeAddress: string
  blacklistedSources: number[]
  referrer: string
  referrerFee: number
}

const BASE_CONFIG: OpenOceanConfig = {
  swapEndpoint: "https://open-api.openocean.finance/v4/base/swap",
  gasEndpoint: "https://open-api.openocean.finance/v4/base/gasPrice",
  nativeAddress: "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE",
  blacklistedSources: [12, 40, 3],
  referrer: "0x9a134d3bdeb95c8c8773bd7627f1fdd792531c97",
  referrerFee: 0.01,
}

const getOpenOceanChainConfig = (chainId: number): OpenOceanConfig => {
  switch (chainId) {
    case 8453:
      return BASE_CONFIG
    default:
      throw new Error(`Chain ${chainId} not supported by OpenOcean`)
  }
}

/**
 * OpenOcean Swap Plugin
 * Implements ISwapAggregatorPlugin for OpenOcean aggregator
 */
export class OpenOceanSwapPlugin implements ISwapAggregatorPlugin {
  readonly id = "openocean"
  readonly name = "OpenOcean"
  readonly displayName = "OpenOcean"
  readonly description = "Cross-chain DEX aggregator with competitive rates"
  readonly icon = "/brands/openocean-icon.svg"
  readonly color = "from-cyan-500 to-blue-500"
  readonly isProductionReady = true

  // Quote state (owned by aggregator)
  latestQuote: NormalizedSwapQuote | null = null
  selectedQuote: NormalizedSwapQuote | null = null

  // Quote request state (stored from events)
  private currentQuoteRequest: SwapQuoteRequestData | null = null

  // Internal state
  private quoteQuery: OrchestratedQuery<void> | null = null
  private initialized = false

  // Dependencies (set during initialize)
  private orchestratedQueryStore: OrchestratedQueryStore | null = null
  private eventBus: IEventBus | null = null
  private swapCoordinator: ISwapCoordinator | null = null
  private inputStore: ISwapInputStore | null = null
  private priceImpactCalculatorParam: IPriceImpactCalculator | null = null
  private chainId: number | null = null
  private nativeToken: TokenStore | null = null
  private walletStore: IWalletConnectionStore | null = null
  private slippageBps: number | null = null
  private settingsStore: ISwapAggregatorSettingsStore | null = null
  private config: any = null // Wagmi config for readContract

  constructor(
    private readonly priceImpactCalculator: IPriceImpactCalculator,
    private readonly getNativeTokenPrice: () => number,
    private readonly getConfig: () => any, // Wagmi config getter
  ) {
    makeObservable(this, {
      latestQuote: observable,
      selectedQuote: observable,
      selectCurrentQuote: action,
      updateSelectedQuote: action,
      clearSelection: action,
      clearQuotes: action,
      displayQuote: computed,
      hasBetterQuote: computed,
      isLoadingQuotes: computed,
    })
  }

  /**
   * Check if the plugin's quote query is currently running
   */
  get isLoadingQuotes(): boolean {
    return this.quoteQuery?.isRunning ?? false
  }

  supportsDirectTransaction(): boolean {
    return true // Transaction data included in quote
  }

  /**
   * Initialize the aggregator with its own orchestrator and query cycle
   */
  initialize(
    orchestratedQueryStore: OrchestratedQueryStore,
    eventBus: IEventBus,
    swapCoordinator: ISwapCoordinator,
    inputStore: ISwapInputStore,
    priceImpactCalculator: IPriceImpactCalculator,
    chainId: number,
    nativeToken: TokenStore,
    walletStore: IWalletConnectionStore,
    slippageBps: number,
    settingsStore: ISwapAggregatorSettingsStore,
    _apiTransport?: any,
    _onChainTransport?: any,
  ): void {
    if (this.initialized) {
      throw new Error("OpenOcean plugin already initialized")
    }

    this.orchestratedQueryStore = orchestratedQueryStore
    this.eventBus = eventBus
    this.swapCoordinator = swapCoordinator
    this.inputStore = inputStore
    this.priceImpactCalculatorParam = priceImpactCalculator
    this.chainId = chainId
    this.nativeToken = nativeToken
    this.walletStore = walletStore
    this.slippageBps = slippageBps
    this.settingsStore = settingsStore
    this.config = this.getConfig()

    // Create quote query with OpenOcean-specific interval (10 seconds)
    // Rate limiting: OpenOcean allows 2 r/s, so we enforce 1 r/s (1000ms) to be safe
    // This prevents brutal 1-hour bans if we accidentally exceed the limit
    const rateLimitConfig: RateLimitConfig = {
      minIntervalMs: 1000, // 1 second minimum between executions
    }

    const queryName = `OpenOcean Quotes`
    this.quoteQuery = new OrchestratedQuery<void>(
      queryName,
      (_query) => this.fetchQuoteInternal(), // Query parameter available but not used (no chunking)
      10_000, // 10 seconds polling interval
      this.onQuoteSuccess.bind(this),
      this.onQuoteFailed.bind(this),
      orchestratedQueryStore,
      rateLimitConfig, // Rate limiting enforced at OrchestratedQuery level
    )

    // Register query with coordinator
    swapCoordinator.registerQuoteQuery(queryName)

    // Subscribe to quote request events
    eventBus.subscribe(SwapQuoteRequestedEvent.TYPE, this.handleQuoteRequested.bind(this))
    eventBus.subscribe(SwapQuoteRequestClearedEvent.TYPE, this.handleQuoteRequestCleared.bind(this))
    eventBus.subscribe(SwapAggregatorEnabledEvent.TYPE, this.handleAggregatorEnabled.bind(this))
    eventBus.subscribe(SwapAggregatorDisabledEvent.TYPE, this.handleAggregatorDisabled.bind(this))

    // Do NOT start query during initialization - wait for events
    this.initialized = true
  }

  /**
   * Check if aggregator is enabled
   */
  private isEnabled(): boolean {
    if (!this.settingsStore) return false
    const aggregator = this.settingsStore.SwapAggregators.find(this.id)
    if (this.settingsStore.SwapAggregators.fallbackMode === "multi") {
      return Boolean(aggregator?.enabled)
    }
    return aggregator?.enabled || this.settingsStore.SwapAggregators.activeFallbackAggregatorId === this.id || false
  }

  supportsApiTransport(): boolean {
    return true
  }

  supportsOnChainTransport(): boolean {
    return false
  }

  /**
   * Internal quote fetching (called by OrchestratedQuery)
   */
  private async fetchQuoteInternal(): Promise<void> {
    if (!this.walletStore || !this.priceImpactCalculatorParam || !this.currentQuoteRequest) {
      return
    }

    // Get user address dynamically
    const userAddress = this.walletStore.safeLensAddress

    const { inputToken, outputToken, inputAmount, slippageBps, chainId, nativeToken } = this.currentQuoteRequest

    if (!inputToken || !outputToken || !inputAmount || inputAmount === "0") {
      return
    }

    try {
      // Validate amount format (matching Fly plugin pattern)
      if (inputAmount === "." || inputAmount.endsWith(".")) {
        return
      }

      // Parse amount using token's parseUnits method (tied to token decimals)
      let inputAmountRaw: bigint
      try {
        inputAmountRaw = inputToken.parseUnits(inputAmount)
      } catch (e) {
        // Invalid amount format
        return
      }

      // Validate amount string length (matching old implementation)
      const amountStr = inputAmountRaw.toString().replace(/n$/, "")
      if (amountStr.length > 160) {
        return
      }

      // Build request
      const request: AggregatorQuoteRequest = {
        chainId,
        inputToken,
        outputToken,
        inputAmountRaw,
        inputAmountFormatted: inputAmount,
        slippageBps,
        userAddress,
        nativeToken,
      }

      // Fetch quote (internal method that returns the quote)
      const normalizedQuote = await this.fetchQuoteInternalMethod(request)

      if (normalizedQuote) {
        runInAction(() => {
          this.latestQuote = normalizedQuote
        })

        // Publish event
        if (this.eventBus) {
          this.eventBus.publish(new SwapQuoteReceivedEvent(normalizedQuote))
        }
      }
    } catch (error) {
      console.error(`[OpenOcean] Error fetching quote:`, error)
    }
  }

  /**
   * Quote query success handler
   */
  private onQuoteSuccess(_sender: IOrchestratedQuerySender<void>, _data: void): void {
    // Quote already updated in fetchQuoteInternal
    this.quoteQuery?.stop()
  }

  /**
   * Quote query failure handler
   */
  private onQuoteFailed(_sender: IOrchestratedQuerySender<void>, _error: Error): void {
    this.quoteQuery?.stop()
    console.error("[OpenOcean] quote query failed:", _error)
  }

  selectCurrentQuote(): void {
    if (this.latestQuote) {
      this.selectedQuote = this.latestQuote
    }
  }

  updateSelectedQuote(quote: NormalizedSwapQuote): void {
    this.selectedQuote = quote
  }

  clearSelection(): void {
    this.selectedQuote = null
  }

  clearQuotes(): void {
    runInAction(() => {
      this.latestQuote = null
      this.selectedQuote = null
    })
  }

  /**
   * Event handlers for quote request events
   */
  private handleQuoteRequested(event: SwapQuoteRequestedEvent): void {
    // Store the quote request state
    runInAction(() => {
      this.currentQuoteRequest = event.quoteRequest
    })

    // If plugin is enabled, start query and force execute immediately
    if (this.isEnabled() && this.quoteQuery) {
      if (!this.quoteQuery.isActive) {
        this.quoteQuery.start()
      } else if (!this.quoteQuery.isRunning) {
        this.quoteQuery.forceExecute()
      }
    }
  }

  private handleQuoteRequestCleared(_event: SwapQuoteRequestClearedEvent): void {
    // Stop query if active
    if (this.quoteQuery?.isActive) {
      this.quoteQuery.stop()
    }

    // Clear stored state and quotes
    runInAction(() => {
      this.currentQuoteRequest = null
      this.latestQuote = null
      this.selectedQuote = null
    })
  }

  private handleAggregatorEnabled(event: SwapAggregatorEnabledEvent): void {
    // Only react if this event is for this plugin
    if (event.aggregatorId !== this.id) {
      return
    }

    // Check if we have a valid stored quote request
    if (this.currentQuoteRequest && this.quoteQuery) {
      // Start query and force execute immediately
      if (!this.quoteQuery.isActive) {
        this.quoteQuery.start()
      } else if (!this.quoteQuery.isRunning) {
        this.quoteQuery.forceExecute()
      }
    }
  }

  private handleAggregatorDisabled(event: SwapAggregatorDisabledEvent): void {
    // Only react if this event is for this plugin
    if (event.aggregatorId !== this.id) {
      return
    }

    // Stop query but keep stored state (user might re-enable later)
    if (this.quoteQuery?.isActive) {
      this.quoteQuery.stop()
    }
  }

  get displayQuote(): NormalizedSwapQuote | null {
    return this.selectedQuote ?? this.latestQuote
  }

  get hasBetterQuote(): boolean {
    if (!this.selectedQuote || !this.latestQuote) {
      return false
    }
    return this.latestQuote.outputAmountRaw > this.selectedQuote.outputAmountRaw
  }

  async getAllowance(token: TokenStore, spender: Address, userAddress: Address): Promise<bigint> {
    if (!this.config || !this.chainId) {
      return 0n
    }

    try {
      const result = await readContract(this.config, {
        address: token.address.toViemAddress(),
        abi: erc20Abi,
        functionName: "allowance",
        args: [userAddress, spender],
        chainId: this.chainId,
      })

      return result as bigint
    } catch (error) {
      console.error("Error getting allowance:", error)
      return 0n
    }
  }

  async executeAllowanceTransaction(
    quote: NormalizedSwapQuote,
    currentAllowance: bigint,
    userAddress: Address,
    approvalMode: ApprovalMode,
    contractRiskLevel: ContractRiskLevel,
  ): Promise<NormalizedSwapTransaction | null> {
    if (!this.eventBus) {
      throw new Error("OpenOcean plugin not initialized")
    }

    try {
      // Publish approval started event
      this.eventBus.publish(new SwapApprovalStartedEvent(quote))

      // OpenOcean includes transaction data in quote, so target router is available immediately
      if (!quote.transactionData) {
        throw new Error("Transaction data not found in quote")
      }

      const targetAddress = quote.transactionData.to

      // Check if approval is needed
      if (currentAllowance >= quote.inputAmountRaw) {
        // Already approved, publish completion event
        this.eventBus.publish(
          new SwapApprovalCompletedEvent(quote, "0x0000000000000000000000000000000000000000000000000000000000000000"),
        )
        return null
      }

      // Calculate approval amount based on approval mode and contract risk level
      const approvalAmount = determineApprovalAmount(quote.inputAmountRaw, approvalMode, contractRiskLevel)

      // Encode approve(address spender, uint256 amount)
      // Function selector: approve(address,uint256) = 0x095ea7b3
      const spenderAddress = targetAddress.toLowerCase().replace("0x", "").padStart(64, "0")
      const amountHex = approvalAmount.toString(16).padStart(64, "0")
      const data = ("0x095ea7b3" + spenderAddress + amountHex) as `0x${string}`

      const approvalTransaction: NormalizedSwapTransaction = {
        to: quote.inputToken.address.toViemAddress(),
        data: data as `0x${string}`,
        value: 0n,
        gasLimit: BigInt(50000), // Standard ERC20 approval gas
      }

      return approvalTransaction
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error"
      if (this.eventBus) {
        this.eventBus.publish(new SwapErrorEvent(quote, errorMessage))
      }
      throw error
    }
  }

  async executeSwapTransaction(
    quote: NormalizedSwapQuote,
    _userAddress: Address,
  ): Promise<NormalizedSwapTransaction | null> {
    // OpenOcean includes transaction data in quote, so we can return it directly
    return this.buildTransaction(quote, {
      quote,
      userAddress: _userAddress,
    })
  }

  async fetchQuote(params: AggregatorQuoteRequest): Promise<void> {
    await this.fetchQuoteInternalMethod(params)
  }

  /**
   * Internal method that fetches quote and returns NormalizedSwapQuote
   * Rate limiting is handled by OrchestratedQuery, so we don't need to manage it here
   */
  private async fetchQuoteInternalMethod(params: AggregatorQuoteRequest): Promise<NormalizedSwapQuote | null> {
    try {
      const openOceanConfig = getOpenOceanChainConfig(params.chainId)

      // Convert token addresses - OpenOcean uses different native address format
      const isInputNative = params.inputToken.address.equalsString(params.nativeToken.address.toString())
      const isOutputNative = params.outputToken.address.equalsString(params.nativeToken.address.toString())

      const inputTokenAddress = isInputNative
        ? openOceanConfig.nativeAddress
        : params.inputToken.address.toViemAddress()

      const outputTokenAddress = isOutputNative
        ? openOceanConfig.nativeAddress
        : params.outputToken.address.toViemAddress()

      // Fetch gas price first
      // Note: Rate limiting is handled by OrchestratedQuery, ensuring at least 1 second between executions
      // Since each execution makes 2 API calls (gas price + quote), we need to ensure they're spaced out
      // However, within a single execution, we can make multiple calls quickly
      // The rate limiter ensures the NEXT execution waits if needed
      let gasPrice = 0
      try {
        const gasPriceRequest = await fetch(openOceanConfig.gasEndpoint, {
          mode: "cors",
          credentials: "omit",
        })
        if (gasPriceRequest.ok) {
          const gasPriceResponse = (await gasPriceRequest.json()) as unknown
          const parsedGasPriceResponse = openOceanGasPriceResponse.safeParse(gasPriceResponse)
          if (parsedGasPriceResponse.success) {
            gasPrice = parsedGasPriceResponse.data.without_decimals.standard
          }
        }
      } catch (error) {
        console.warn("[OpenOcean] Failed to fetch gas price; quote will use fallback gas price.", error)
      }

      // Build quote URL
      const swapUrl = new URL(openOceanConfig.swapEndpoint)
      swapUrl.searchParams.append("inTokenAddress", inputTokenAddress)
      swapUrl.searchParams.append("outTokenAddress", outputTokenAddress)
      swapUrl.searchParams.append("amount", params.inputAmountFormatted)
      swapUrl.searchParams.append("gasPrice", gasPrice.toString())
      swapUrl.searchParams.append("slippage", (params.slippageBps / 100).toString())
      swapUrl.searchParams.append("account", params.userAddress)
      swapUrl.searchParams.append("disabledDexIds", openOceanConfig.blacklistedSources.join(","))
      swapUrl.searchParams.append("referrer", openOceanConfig.referrer)
      swapUrl.searchParams.append("referrerFee", openOceanConfig.referrerFee.toString())

      // Fetch quote
      // Rate limiting between executions is handled by OrchestratedQuery
      const quoteRequest = await fetch(swapUrl, {
        mode: "cors",
        credentials: "omit",
      })

      if (!quoteRequest.ok) {
        return null
      }

      const quoteResponseBody = (await quoteRequest.json()) as unknown
      const parsedQuoteResponse = openOceanQuoteResponse.safeParse(quoteResponseBody)
      if (!parsedQuoteResponse.success) {
        console.warn("[OpenOcean] Quote response did not match the expected shape.")
        return null
      }

      if (parsedQuoteResponse.data.code !== 200 || !parsedQuoteResponse.data.data) {
        return null
      }

      const quoteData = parsedQuoteResponse.data.data

      // Parse amounts
      const inputAmountRaw = params.inputAmountRaw
      const outputAmountRaw = BigInt(quoteData.outAmount)

      // Calculate exchange rate
      const exchangeRate = inputAmountRaw > 0n ? (Number(outputAmountRaw) / Number(inputAmountRaw)).toString() : "0"

      // Calculate price impact
      const inputTokenPrice = params.inputToken.bestPrice
      const outputTokenPrice = params.outputToken.bestPrice
      const inputAmountUsd = Number(params.inputAmountFormatted) * inputTokenPrice
      const outputAmountFormatted = params.outputToken.formatUnits(outputAmountRaw)
      const outputAmountUsd = Number(outputAmountFormatted) * outputTokenPrice

      // Subtract gas cost from output value (OpenOcean includes this in their calculation)
      const nativePrice = this.getNativeTokenPrice()
      const formattedGas = formatUnits(BigInt(quoteData.estimatedGas), 9)
      const gasUsedValueUSD = Number(formattedGas) * nativePrice
      const netOutputAmountUsd = outputAmountUsd - gasUsedValueUSD

      const priceImpactBps = this.priceImpactCalculator.calculate(inputAmountUsd, netOutputAmountUsd)

      // Get gas estimate
      const gasLimit = BigInt(quoteData.estimatedGas)

      // Build normalized quote with transaction data included
      const normalizedQuote: NormalizedSwapQuote = {
        aggregatorId: this.id,
        quoteId: `${this.id}-${Date.now()}`, // OpenOcean doesn't provide a quote ID
        inputToken: params.inputToken,
        outputToken: params.outputToken,
        inputAmountRaw,
        outputAmountRaw,
        inputAmountFormatted: params.inputAmountFormatted,
        outputAmountFormatted,
        exchangeRate,
        priceImpactBps,
        gasEstimate: {
          gasLimit,
          estimatedCostWei: BigInt(Math.round(gasUsedValueUSD / nativePrice) * 1e9), // Approximate
        },
        transactionData: {
          to: quoteData.to as `0x${string}`,
          data: quoteData.data as `0x${string}`,
          value: BigInt(quoteData.value),
        },
        transactionMetadata: {
          targetAddress: quoteData.to as `0x${string}`, // Store target router for allowance checking
        },
        timestamp: unixTimestamp(),
      }

      return normalizedQuote
    } catch (error) {
      console.error("Error fetching OpenOcean quote:", error)
      return null
    }
  }

  async buildTransaction(
    quote: NormalizedSwapQuote,
    _params: AggregatorTransactionRequest,
  ): Promise<NormalizedSwapTransaction | null> {
    // OpenOcean includes transaction data in quote, so we can return it directly
    if (quote.transactionData) {
      return {
        to: quote.transactionData.to,
        data: quote.transactionData.data,
        value: quote.transactionData.value,
        gasLimit: quote.gasEstimate?.gasLimit ?? BigInt(500000),
      }
    }

    return null
  }
}
